/**
 * Sprint 22 — ToolCallCard (ported from Sprint 3.2, deep-links repointed to v3
 * routes). Renders inline whenever the agent invokes a Kuvio MCP tool: humanised
 * name, a one-line result summary, and a "View →" deep-link when the tool
 * produced or referenced an entity / library node. Wired into
 * <MessagePrimitive.Parts components.tools.Fallback> so it catches every tool
 * call regardless of name — transparency is the supervisor-cockpit win.
 */

import { ChevronRight, Loader2 } from "@icons";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { ToolCallMessagePartProps } from "@assistant-ui/react";
import { invalidateForTool } from "../../../lib/mutation-registry";
import { useDelegationSession, type DelegationTraceStep } from "../../hooks/use-delegations";

// Sprint 24.7 — the deferred-schema loader tool (mirrors the worker's
// TOOL_SEARCH_TOOL_NAME). Rendered specially so it reads as plumbing, not content.
const TOOL_SEARCH_TOOL_NAME = "tool_search";

/**
 * Pull the loaded tool names out of tool_search's result. The worker appends a
 * `<system-reminder>` confirming "…now schema-loaded and callable: a, b, c"; we
 * parse that line. Falls back to [] (the chip then just shows a count of 0).
 */
function parseLoadedToolNames(resultText: string): string[] {
  const m = resultText.match(/now schema-loaded and callable:\s*([^\n<]+)/i);
  if (!m || !m[1]) return [];
  return m[1]
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Tool-name → human label. Falls back to a snake_case → Title Case mapping.
// ---------------------------------------------------------------------------

const TOOL_LABELS: Record<string, string> = {
  get_workspace_info: "Workspace info",
  list_entity_types: "List entity types",
  create_entity_type: "Create entity type",
  update_entity_type: "Update entity type",
  delete_entity_type: "Delete entity type",
  set_workspace_instructions: "Set workspace instructions",
  update_workspace_name: "Rename workspace",
  get_workspace_context: "Get context",
  set_workspace_context: "Set context",
  create_agent: "Create agent",
  create_page: "Create page",
  create_memory: "Create memory",
  move_node: "Move node",
  get_node: "Get node",
  list_tree: "List tree",
  update_node: "Update node",
  archive_node: "Archive node",
  find_related: "Find related",
  list_tasks: "List tasks",
  create_task: "Add task",
  list_chat_sessions: "List chat sessions",
  compose_retrospective: "Compose retrospective",
  find_patterns: "Find patterns",
  check_task_against_learnings: "Check against learnings",
  search_memories_semantic: "Search memories",
  get_ai_usage: "AI usage",
  delegate_to_agent: "Delegate to agent",
};

function humaniseToolName(name: string): string {
  if (TOOL_LABELS[name]) return TOOL_LABELS[name];
  // Strip leading verb (create_/list_/get_/update_/archive_) and snake → Title.
  const parts = name.split("_");
  return parts
    .map((p, i) => (i === 0 ? p.charAt(0).toUpperCase() + p.slice(1) : p))
    .join(" ");
}

// ---------------------------------------------------------------------------
// Result summary + entity-id extraction
// ---------------------------------------------------------------------------

interface MaybeStructured {
  structuredContent?: unknown;
  content?: Array<{ type?: string; text?: string }>;
  entities?: Array<{ id?: string }>;
  entity?: { id?: string };
  node?: { id?: string };
  nodes?: Array<{ id?: string }>;
  id?: string;
  error?: string;
  isError?: boolean;
}

function summariseResult(toolName: string, result: unknown): string | null {
  if (result == null) return null;
  if (typeof result !== "object") return String(result);
  const r = result as MaybeStructured;
  if (r.isError || r.error) {
    return `error: ${r.error ?? "see message"}`;
  }
  const sc = (r.structuredContent ?? r) as MaybeStructured & {
    entities?: unknown[];
    nodes?: unknown[];
    entity_types?: unknown[];
    conversations?: unknown[];
  };
  if (Array.isArray(sc.entities)) {
    return `${sc.entities.length} ${sc.entities.length === 1 ? "entity" : "entities"}`;
  }
  if (Array.isArray(sc.entity_types)) {
    return `${sc.entity_types.length} entity types`;
  }
  if (Array.isArray(sc.conversations)) {
    return `${sc.conversations.length} session${sc.conversations.length === 1 ? "" : "s"}`;
  }
  if (Array.isArray(sc.nodes)) {
    return `${sc.nodes.length} ${sc.nodes.length === 1 ? "node" : "nodes"}`;
  }
  if (sc.entity?.id) return sc.entity.id;
  if (sc.node?.id) return sc.node.id;
  if (sc.id) return sc.id;
  if (toolName.startsWith("create_")) return "created";
  if (toolName.startsWith("update_")) return "updated";
  if (toolName.startsWith("archive_")) return "archived";
  return null;
}

function extractDeepLink(
  toolName: string,
  args: unknown,
  result: unknown,
): { kind: "entity" | "node"; slug?: string; id: string } | null {
  const sc =
    (result as MaybeStructured)?.structuredContent ??
    (result as MaybeStructured);
  const sco = (sc ?? {}) as MaybeStructured;

  // Library node tools — link to the node view.
  if (
    toolName === "create_agent" ||
    toolName === "create_page" ||
    toolName === "create_memory" ||
    toolName === "get_node" ||
    toolName === "update_node" ||
    toolName === "move_node"
  ) {
    const id = sco.node?.id ?? (args as { id?: string })?.id;
    if (id) return { kind: "node", id };
  }

  // Entity creators / getters — link to entity detail.
  if (toolName.startsWith("create_") || toolName.startsWith("get_") || toolName.startsWith("update_")) {
    const slug = toolName.replace(/^(create|get|update)_/, "").replace(/_/g, "-");
    const id = sco.entity?.id ?? sco.id ?? (args as { id?: string })?.id;
    if (id && typeof id === "string" && /^[A-Z]+-\d+/.test(id)) {
      return { kind: "entity", slug, id };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ToolCallCard(props: ToolCallMessagePartProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toolName, args, result, status } = props;
  const isRunning = status.type === "running";
  const isError = props.isError === true;

  // Sprint 6.0a — reactive query invalidation so the rest of the UI reflects
  // what the agent just did, the moment the tool completes.
  const invalidatedRef = useRef(false);
  useEffect(() => {
    if (isRunning || invalidatedRef.current) return;
    invalidatedRef.current = true;
    invalidateForTool(toolName, qc);
  }, [isRunning, toolName, qc]);

  // Sprint 24.7 — expand/collapse for the tool_search schema body (hidden by default).
  const [expanded, setExpanded] = useState(false);

  // Sprint 24.7 — tool_search is plumbing, not content: render it as a quiet
  // "loading schema" chip instead of dumping the raw <functions> JSON into chat.
  // The loaded tool names are parsed from the worker's post-call confirmation
  // reminder; the full schema block stays available behind an expander.
  if (toolName === TOOL_SEARCH_TOOL_NAME) {
    const query = (args as { query?: string } | undefined)?.query ?? "";
    const resultText = typeof result === "string" ? result : "";
    const loaded = parseLoadedToolNames(resultText);
    const summaryLabel = isRunning
      ? "Loading tool schemas…"
      : loaded.length > 0
        ? `Loaded ${loaded.length} tool schema${loaded.length === 1 ? "" : "s"}`
        : "No matching tools";
    return (
      <div
        className="my-2 inline-flex max-w-full flex-col gap-1 rounded-xl glass-panel bg-white/5 px-3 py-2 font-caption text-caption"
      >
        <div className="inline-flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-black/30 px-2 py-1 font-mono text-[0.625rem] text-on-surface">
            {isRunning ? <Loader2 size={10} className="animate-spin" /> : <i className="ti ti-search" style={{ fontSize: 11 }} aria-hidden="true" />}
            tool_search
          </span>
          <span className="text-on-surface-variant truncate">{summaryLabel}</span>
          {!isRunning && (loaded.length > 0 || resultText) ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="ml-auto inline-flex items-center gap-0.5 text-secondary hover:text-primary transition-colors"
            >
              {expanded ? "Hide" : "Details"}
              <ChevronRight size={11} style={{ transform: expanded ? "rotate(90deg)" : undefined, transition: "transform 0.15s" }} />
            </button>
          ) : null}
        </div>
        {expanded ? (
          <div className="mt-1 flex flex-col gap-1 text-[0.625rem] text-on-surface-variant">
            {query ? <span className="font-mono opacity-70">query: {query}</span> : null}
            {loaded.length > 0 ? (
              <span>
                Now callable: {loaded.join(", ")}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  // Sprint 24.8 — delegate_to_agent renders as a rich card (sub-agent name, task,
  // status, cost) with a "view trace" expander. Split into its own component so the
  // trace query hook obeys the rules of hooks regardless of this card's branch.
  if (toolName === "delegate_to_agent") {
    return <DelegateToAgentCard {...props} />;
  }

  // Sprint 23.5 — composition tools are intercepted client-side and patched
  // into the form panel. Replace the noisy full card with a tiny confirmation
  // chip so the user can see the AI is filling fields without flooding the
  // chat: "✓ name", "✓ bio", etc.
  if (toolName === "update_composition_draft") {
    const fieldName = (args as { field?: string } | undefined)?.field;
    if (!fieldName) return null;
    return (
      <span
        className="ml-0 mr-2 mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
        style={{
          color: "var(--color-text-tertiary)",
          background: "var(--color-background-secondary)",
          border: "var(--kuvio-border-default)",
        }}
        title={`Filled ${fieldName}`}
      >
        {isRunning ? (
          <Loader2 size={9} className="animate-spin" aria-hidden="true" />
        ) : isError ? (
          <i className="ti ti-alert-triangle" style={{ fontSize: 9, color: "var(--color-text-danger)" }} aria-hidden="true" />
        ) : (
          <i className="ti ti-check" style={{ fontSize: 9 }} aria-hidden="true" />
        )}
        {fieldName}
      </span>
    );
  }

  const label = humaniseToolName(toolName);
  const summary = summariseResult(toolName, result);
  const link = !isRunning && !isError ? extractDeepLink(toolName, args, result) : null;

  const onView = () => {
    if (!link) return;
    // Sprint 22 — v3 routes: library nodes at /library/:id, rows at /entities/:slug/:id.
    if (link.kind === "node") navigate(`/library/${link.id}`);
    else if (link.kind === "entity" && link.slug) {
      navigate(`/entities/${link.slug}/${link.id}`);
    }
  };

  return (
    <div className="my-2 inline-flex max-w-full items-center gap-2 rounded-xl glass-panel bg-white/5 px-3 py-2 font-caption text-caption">
      <span
        className={
          "inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-mono text-[0.625rem] " +
          (isError ? "bg-error/15 text-error" : "bg-black/30 text-on-surface")
        }
      >
        {isRunning ? <Loader2 size={10} className="animate-spin" /> : null}
        {label}
      </span>
      {summary ? <span className="text-on-surface-variant truncate">{summary}</span> : null}
      {link ? (
        <button
          type="button"
          onClick={onView}
          className="ml-auto inline-flex items-center gap-0.5 text-secondary hover:text-primary transition-colors"
        >
          View
          <ChevronRight size={11} />
        </button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sprint 24.8 — delegate_to_agent card. Shows the specialist, the task, live
// status, and cost; expands to the sub-agent's trace (fetched from the persisted
// delegation session, so the coordinator's model never carries it — Q5/Q20).
// ---------------------------------------------------------------------------

interface DelegationResultShape {
  success?: boolean;
  subagent_node_id?: string;
  subagent_name?: string | null;
  description?: string | null;
  result?: string;
  error?: string;
  error_code?: string;
  timed_out?: boolean;
  cost_usd?: number;
  tokens_in?: number;
  tokens_out?: number;
  tool_calls?: number;
  depth?: number;
  conversation_id?: string | null;
}

function extractDelegation(result: unknown): DelegationResultShape {
  if (!result || typeof result !== "object") return {};
  const r = result as MaybeStructured;
  return ((r.structuredContent ?? r) as DelegationResultShape) ?? {};
}

function formatCost(n: number): string {
  if (n <= 0) return "$0.00";
  return n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;
}

function DelegateToAgentCard(props: ToolCallMessagePartProps) {
  const { args, result, status } = props;
  const [expanded, setExpanded] = useState(false);

  const dr = extractDelegation(result);
  const isRunning = status.type === "running";
  const failed = !isRunning && (dr.success === false || props.isError === true);
  const done = !isRunning && !failed;

  const a = (args ?? {}) as { subagent?: string; task?: string; description?: string };
  const task = a.task ?? dr.description ?? "";
  const name = dr.subagent_name ?? a.description ?? "specialist";

  // Only fetch the trace when the user opens it.
  const session = useDelegationSession(expanded ? dr.conversation_id : null);

  const statusLabel = isRunning ? "working…" : failed ? "failed" : "done";
  const canExpand = !isRunning && Boolean(dr.conversation_id || dr.result || dr.error);

  return (
    <div className="my-2 flex max-w-full flex-col gap-1 rounded-xl glass-panel bg-white/5 px-3 py-2 font-caption text-caption">
      <div className="inline-flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-black/30 px-2 py-1 font-mono text-[0.625rem] text-on-surface">
          {isRunning ? (
            <Loader2 size={10} className="animate-spin" />
          ) : (
            <i className="ti ti-users" style={{ fontSize: 11 }} aria-hidden="true" />
          )}
          delegate
        </span>
        {!isRunning ? <span className="text-on-surface truncate font-medium">{name}</span> : null}
        <span className={"truncate " + (failed ? "text-error" : "text-on-surface-variant")}>
          {statusLabel}
        </span>
        {done && typeof dr.cost_usd === "number" ? (
          <span className="text-on-surface-variant">{formatCost(dr.cost_usd)}</span>
        ) : null}
        {canExpand ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="ml-auto inline-flex items-center gap-0.5 text-secondary hover:text-primary transition-colors"
          >
            {expanded ? "Hide" : "View trace"}
            <ChevronRight
              size={11}
              style={{ transform: expanded ? "rotate(90deg)" : undefined, transition: "transform 0.15s" }}
            />
          </button>
        ) : null}
      </div>

      {task ? (
        <span className="text-on-surface-variant truncate">
          {isRunning ? `Delegating: ${task}` : task}
        </span>
      ) : null}

      {failed && dr.error ? <span className="text-error">{dr.error}</span> : null}

      {expanded ? (
        <div className="mt-1 flex flex-col gap-1.5 border-t border-white/10 pt-1.5 text-[0.6875rem] text-on-surface-variant">
          {(typeof dr.tokens_in === "number" || typeof dr.tool_calls === "number") ? (
            <span className="opacity-70">
              {typeof dr.tool_calls === "number" ? `${dr.tool_calls} tool call${dr.tool_calls === 1 ? "" : "s"}` : null}
              {typeof dr.tokens_in === "number" ? ` · ${(dr.tokens_in ?? 0) + (dr.tokens_out ?? 0)} tokens` : null}
              {typeof dr.depth === "number" ? ` · depth ${dr.depth + 1}` : null}
            </span>
          ) : null}
          {session.isLoading ? (
            <span className="opacity-60">Loading trace…</span>
          ) : (
            <DelegationTraceView messages={session.data} fallback={dr.result} task={task} />
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Render a delegation's stored trace (user task → per-step tool calls → answer). */
function DelegationTraceView({
  messages,
  fallback,
  task,
}: {
  messages: Array<{ role: string; content: string; trace?: DelegationTraceStep[] }> | undefined;
  fallback: string | undefined;
  task: string;
}) {
  const assistant = messages?.find((m) => m.role === "assistant");
  const steps = assistant?.trace ?? [];
  const answer = assistant?.content ?? fallback ?? "";

  return (
    <div className="flex flex-col gap-1.5">
      {task ? (
        <div>
          <span className="font-medium text-on-surface">Task</span>
          <p className="mt-0.5 whitespace-pre-wrap">{task}</p>
        </div>
      ) : null}
      {steps.length > 0 ? (
        <div className="flex flex-col gap-1">
          {steps.map((s, i) => (
            <div key={i} className="flex flex-col gap-0.5">
              {s.text ? <p className="whitespace-pre-wrap opacity-90">{s.text}</p> : null}
              {s.toolCalls.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {s.toolCalls.map((tc, j) => (
                    <span key={j} className="rounded bg-black/30 px-1.5 py-0.5 font-mono text-[0.625rem] text-on-surface">
                      {tc.name}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {answer ? (
        <div>
          <span className="font-medium text-on-surface">Result</span>
          <p className="mt-0.5 whitespace-pre-wrap">{answer}</p>
        </div>
      ) : null}
    </div>
  );
}
